<template>
  <!-- Minu seaded: what each person sets for themselves, behind their own name
       in the menu. Saved on the server at once (service/userPrefs.js), so it
       follows them to every computer and phone. A new setting is a new section. -->
  <div class="me">
    <div class="me-cap">{{ fullName }}</div>
    <h1 class="me-title">Minu seaded</h1>

    <!-- The four choices are rows that open in place (closed by default); the
         right side of a closed row shows what is chosen now. Open rows are
         remembered in this browser, and /me#<id> opens that row. v-show, not
         v-if: the passkey list loads its keys for the closed row's count. -->
    <section id="kalender" class="me-sec me-row" :class="{ 'me-row--open': isOpen('kalender') }">
      <h2 class="me-row__h">
        <button type="button" class="me-row__head" :aria-expanded="String(isOpen('kalender'))" aria-controls="me-cal-body" @click="toggle('kalender')">
          <span class="me-row__title">Kalendri välimus<span class="me-row__sub">Kuidas töökäsud Kalendris välja näevad</span></span>
          <span class="me-row__val">
            <span class="me-swatch" aria-hidden="true">
              <i v-for="o in samples" :key="o.time" :style="swatchStyle(currentLook, o.color)" />
            </span>
            {{ lookText(currentLook) }}
            <span v-if="prefs.calendarLook === null" class="me-tag">vaikimisi</span>
          </span>
          <span class="me-chev" aria-hidden="true" />
        </button>
      </h2>
      <div v-show="isOpen('kalender')" id="me-cal-body" class="me-row__body">
        <p class="me-hint">Kuidas töökäsud Kalendris välja näevad. Valik kehtib ainult sinule.</p>

        <button
          type="button"
          class="me-default"
          :aria-pressed="String(prefs.calendarLook === null)"
          @click="chooseLook(null)"
        >
          <span class="me-radio" aria-hidden="true" />
          <span><b>Vaikimisi</b> — nagu Seadetes määratud, praegu <b>{{ lookText(defaultLook) }}</b></span>
        </button>

        <div class="me-looks" role="group" aria-label="Kalendri välimus">
          <button
            v-for="look in looks"
            :key="look.value"
            type="button"
            class="me-look"
            :aria-pressed="String(prefs.calendarLook === look.value)"
            @click="chooseLook(look.value)"
          >
            <!-- Two orders drawn the way the Kalender draws them in this look. -->
            <span v-if="look.value === 'vana'" class="me-pre me-pre--vana" aria-hidden="true">
              <span v-for="o in samples" :key="o.time" class="me-vana" :style="{ background: o.color }">
                <b>{{ o.time }}</b><br>{{ o.name }}<br>{{ o.adr }}
              </span>
            </span>
            <span v-else class="me-pre cal-plate" :class="'cal-look-' + look.value" aria-hidden="true">
              <span v-for="o in samples" :key="o.time" class="cal-ev" :style="orderStyle(look, o.color)">
                <span class="event-line cal-ev__time" :style="timeStyle(look, o.color)">
                  <span class="cal-ini" :class="{ 'cal-ini--on': look.fullTime }" :style="initialsStyle(look, o.color)">{{ o.ini }}</span>
                  {{ o.time }}
                </span>
                <span v-if="look.nameLine" class="event-line">{{ o.name }}</span>
                <span class="event-line cal-adr">{{ o.adr }}</span>
                <span class="event-line">{{ o.client }}</span>
              </span>
            </span>
            <span class="me-look__text">
              <b>{{ look.text }}</b>
              <span v-if="look.value === defaultLook" class="me-tag">vaikimisi</span>
              <span class="me-note">{{ look.note }}</span>
            </span>
          </button>
        </div>
      </div>
    </section>

    <section v-if="features.plannerPlate && (isAdmin || isSecretary || isTechnic)" id="planeerija" class="me-sec me-row" :class="{ 'me-row--open': isOpen('planeerija') }">
      <h2 class="me-row__h">
        <button type="button" class="me-row__head" :aria-expanded="String(isOpen('planeerija'))" aria-controls="me-plan-body" @click="toggle('planeerija')">
          <span class="me-row__title">Planeerija<span class="me-row__sub">Uus või vana Planeerija vaade</span></span>
          <span class="me-row__val">{{ prefs.plannerLook === 'vana' ? 'Vana' : 'Uus' }}</span>
          <span class="me-chev" aria-hidden="true" />
        </button>
      </h2>
      <div v-show="isOpen('planeerija')" id="me-plan-body" class="me-row__body">
        <p class="me-hint">Uus on üks kuu valvajate rea ja töökäskudega; vana on kolm kuud järjest nagu enne. Valik kehtib ainult sinule.</p>
        <div class="me-seg me-seg--2" role="group" aria-label="Planeerija">
          <button type="button" :aria-pressed="String(prefs.plannerLook !== 'vana')" @click="choosePlanner('uus')">Uus</button>
          <button type="button" :aria-pressed="String(prefs.plannerLook === 'vana')" @click="choosePlanner('vana')">Vana</button>
        </div>
      </div>
    </section>

    <section v-if="features.intraFrame" id="menuu" class="me-sec me-row" :class="{ 'me-row--open': isOpen('menuu') }">
      <h2 class="me-row__h">
        <button type="button" class="me-row__head" :aria-expanded="String(isOpen('menuu'))" aria-controls="me-menu-body" @click="toggle('menuu')">
          <span class="me-row__title">Menüü asukoht<span class="me-row__sub">Suuremal ekraanil; telefonis ☰ nupu taga</span></span>
          <span class="me-row__val">{{ positionText }}</span>
          <span class="me-chev" aria-hidden="true" />
        </button>
      </h2>
      <div v-show="isOpen('menuu')" id="me-menu-body" class="me-row__body">
        <p class="me-hint">Suuremal ekraanil. Telefonis on menüü alati ☰ nupu taga.</p>
        <div class="me-seg" role="group" aria-label="Menüü asukoht">
          <button
            v-for="option in positions"
            :key="option.value"
            type="button"
            :aria-pressed="String(layout.position === option.value)"
            @click="choosePosition(option.value)"
          >{{ option.text }}</button>
        </div>
      </div>
    </section>

    <!-- Pääsuvõtmed (Süsteem → Turvalisus → Pääsuvõtmed, Intra kasutajad):
         this person's own keys. -->
    <section v-if="features.passkeyUser" id="paasuvotmed" class="me-sec me-row" :class="{ 'me-row--open': isOpen('paasuvotmed') }">
      <h2 class="me-row__h">
        <button type="button" class="me-row__head" :aria-expanded="String(isOpen('paasuvotmed'))" aria-controls="me-keys-body" @click="toggle('paasuvotmed')">
          <span class="me-row__title">Pääsuvõtmed<span class="me-row__sub">Sisselogimine sõrmejälje, näo või Windows Helloga</span></span>
          <span class="me-row__val">
            <template v-if="keyCount === null" />
            <template v-else-if="keyCount">{{ keyCount }} {{ keyCount === 1 ? 'võti' : 'võtit' }}</template>
            <span v-else-if="features.passkeyUserRequired" class="me-tag me-tag--warn">puudub</span>
            <template v-else>Pole lisatud</template>
          </span>
          <span class="me-chev" aria-hidden="true" />
        </button>
      </h2>
      <div v-show="isOpen('paasuvotmed')" id="me-keys-body" class="me-row__body">
        <p class="me-hint">Logi sisse telefoni sõrmejälje või näoga, arvutis Windows Helloga, ilma paroolita.<template v-if="features.passkeyUserRequired"> Intras on pääsuvõti kohustuslik: kui sul on võti, parool üksi enam sisse ei lase.</template><template v-else-if="features.passkeyUserNoPassword"> Kui sul on võti, parool enam sisse ei lase.</template><template v-else> Parool jääb alles.</template></p>
        <passkey-list @changed="keyCount = $event.length" />
      </div>
    </section>

    <!-- The former Seaded page (/settings now leads here): password, the
         signature an akt prefills, and the Google Kalender link. -->
    <div class="me-pair">
      <section id="parool" class="me-sec" :class="{ 'me-sec--alert': passwordPwned > 0 }" aria-labelledby="me-pass">
        <h2 id="me-pass">Muuda parooli</h2>
        <!-- Lekkinud paroolid (server service/pwned.js): asked at a password
             login (at most weekly) and when a password is set. Only while the
             check is on in Turvalisus. -->
        <template v-if="features.pwnedCheck">
          <p v-if="passwordPwned > 0" class="me-alert" role="status">
            Sinu praegune parool on avalikes lekkinud paroolide nimekirjades. Vali uus parool, mida sa mujal ei kasuta.
          </p>
          <p v-else-if="passwordCheckedAt" class="me-pw-state me-pw-state--ok" role="status">
            <v-icon size="18" aria-hidden="true">mdi-shield-check-outline</v-icon>
            Parool kontrollitud {{ checkedDay }}: lekkinud paroolide nimekirjades ei ole.
          </p>
          <p v-else class="me-pw-state" role="status">
            <v-icon size="18" aria-hidden="true">mdi-shield-outline</v-icon>
            Parool pole veel kontrollitud. Kontrollitakse järgmisel parooliga sisselogimisel.
          </p>
        </template>
        <p class="me-hint">Vähemalt 6 tähemärki.</p>
        <v-form ref="changePassword" class="me-form" @submit.prevent="_submit('changePassword')">
          <v-text-field
            v-model="passwordForm.currentPassword"
            type="password"
            autocomplete="current-password"
            :rules="[rules.required]"
            label="Praegune parool"
            variant="outlined"
            density="comfortable"
          />
          <v-text-field
            v-model="passwordForm.password"
            type="password"
            autocomplete="new-password"
            :rules="[rules.required, rules.min(6, 'Parool')]"
            label="Uus parool"
            variant="outlined"
            density="comfortable"
          />
          <v-text-field
            v-model="passwordForm.passwordRepeat"
            type="password"
            autocomplete="new-password"
            :rules="[rules.required, rules.confirmed(() => passwordForm.password, 'Parooli kordus')]"
            label="Korda parooli"
            variant="outlined"
            density="comfortable"
          />
          <button type="submit" class="me-btn" :disabled="submitLoading">{{ submitLoading ? 'Salvestan…' : 'Salvesta' }}</button>
        </v-form>
      </section>

      <div class="me-col">
        <section class="me-sec" aria-labelledby="me-sign">
          <h2 id="me-sign">Allkiri</h2>
          <p class="me-hint">Joonista oma allkiri üks kord — see lisatakse edaspidi aktile tehniku allkirjaks automaatselt. Aktil saab seda alati üle joonistada.</p>
          <div class="me-sign">
            <img v-if="signature" :src="signature" alt="Allkiri">
            <span v-else>Allkiri puudub</span>
          </div>
          <div class="me-actions">
            <button type="button" class="me-btn" :disabled="signatureSaving" @click="signatureDialog = true">{{ signature ? 'Joonista uuesti' : 'Joonista allkiri' }}</button>
            <button v-if="signature" type="button" class="me-btn me-btn--danger" :disabled="signatureSaving" @click="confirmDelete = true">Kustuta</button>
          </div>
        </section>

        <!-- GET /api/user/:id is admin/secretary/tehnic only: anyone else
             simply gets no Google card. -->
        <section v-if="userInfo" class="me-sec" aria-labelledby="me-google">
          <h2 id="me-google">Google Kalender</h2>
          <p class="me-hint">Sinu töökäsud Google Kalendrisse.</p>
          <div class="me-actions">
            <span class="me-state" :class="{ 'me-state--on': userInfo.hasGoogleToken }">{{ userInfo.hasGoogleToken ? 'Autoriseeritud' : 'Autoriseerimata' }}</span>
            <a href="/google/auth" class="me-btn me-btn--outline">{{ userInfo.hasGoogleToken ? 'Autoriseeri uuesti' : 'Autoriseeri' }}</a>
          </div>
        </section>
      </div>
    </div>

    <signature-dialog
      :model-value="signatureDialog"
      title="Allkiri"
      :caption="'Minu seaded · ' + fullName + ' · lisatakse aktile tehniku allkirjaks'"
      :initial="signature"
      @update:model-value="signatureDialog = false"
      @save="saveSignature"
    />
    <confirm
      :visible="confirmDelete"
      title="Kustuta allkiri?"
      action="Kustuta allkiri"
      @confirm="deleteSignature"
      @cancel="confirmDelete = false"
    >
      Salvestatud allkiri kustutatakse. Juba allkirjastatud aktid ei muutu.
    </confirm>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import api from '@/api'
import formMixin from '@/plugins/formMixin'
import rules from '@/plugins/rules'
import loadArchivo from '@/plugins/archivo'
import PasskeyList from '@/components/PasskeyList.vue'
import { LOOKS, defaultLook, lookOf, effectiveLook, orderStyle, timeStyle, initialsStyle } from '@/plugins/calendarLooks'

// The rows that open and close; the ids are also the /me#<id> anchors.
const ROWS = ['kalender', 'planeerija', 'menuu', 'paasuvotmed']
const OPEN_KEY = 'me.open'

function readOpen () {
  try {
    const saved = JSON.parse(localStorage.getItem(OPEN_KEY))
    return Array.isArray(saved) ? saved.filter(id => ROWS.includes(id)) : []
  } catch (err) {
    return []
  }
}

const POSITIONS = [
  { value: 'left', text: 'Vasakul' },
  { value: 'right', text: 'Paremal' },
  { value: 'top', text: 'Üleval' }
]

// Made-up orders for the previews: two technicians, one light and one dark
// colour, so a look's text is seen on both.
const EMPTY_PASSWORD = {
  currentPassword: '',
  password: '',
  passwordRepeat: ''
}

const SAMPLES = [
  { time: '09:00–10:30', ini: 'DV', name: 'Denis Vahtra', color: '#8ac25d', adr: 'Metsa 44, Tallinn', client: 'Tamme 40 KÜ' },
  { time: '10:00–11:30', ini: 'IR', name: 'Ivar Reinbusch', color: '#443c85', adr: 'Liiva 2-6, Viimsi', client: 'Anu Lepp' }
]

export default {
  components: { PasskeyList },
  // _submit/handleForm/successMessage belong to the password form; the
  // signature card runs its own async.
  mixins: [ formMixin ],
  data () {
    return {
      rules,
      passwordForm: { ...EMPTY_PASSWORD },
      userInfo: null,
      // The saved signature as a data URL, or null when there is none.
      signature: null,
      signatureDialog: false,
      signatureSaving: false,
      confirmDelete: false,
      // Rows open now; null until the passkey list has loaded its keys.
      open: readOpen(),
      keyCount: null
    }
  },
  computed: {
    ...mapState(['user', 'features', 'prefs', 'layout', 'passwordPwned', 'passwordCheckedAt']),
    checkedDay () {
      const d = new Date(this.passwordCheckedAt)
      return isNaN(d) ? '' : d.toLocaleDateString('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    ...mapGetters(['isAdmin', 'isSecretary', 'isTechnic']),
    successMessage () {
      return 'Parool edukalt muudetud!'
    },
    looks: () => LOOKS,
    samples: () => SAMPLES,
    positions: () => POSITIONS,
    fullName () {
      return (this.user && this.user.fullName) || ''
    },
    defaultLook () {
      return defaultLook(this.features)
    },
    currentLook () {
      return effectiveLook(this.prefs, this.features)
    },
    positionText () {
      const option = POSITIONS.find(o => o.value === this.layout.position)
      return option ? option.text : POSITIONS[0].text
    }
  },
  watch: {
    // A link to /me#paasuvotmed (or a click on one while here) opens that row;
    // /me#parool and the rows are scrolled to.
    '$route.hash': {
      immediate: true,
      handler (hash) {
        const id = (hash || '').replace('#', '')
        if (ROWS.includes(id) && !this.open.includes(id)) this.open = [...this.open, id]
        if (id) {
          this.$nextTick(() => {
            const el = document.getElementById(id)
            if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' })
          })
        }
      }
    }
  },
  created () {
    loadArchivo()
    this.getPrefs()
    // Each card on its own: a 403 on the user (an employee with no role) must
    // not also cost the signature card.
    api.getMySignature().then(mine => {
      this.signature = (mine && mine.signature) || null
    }).catch(() => {})
    if (this.user && this.user.userId) {
      api.getUser(this.user.userId).then(info => {
        this.userInfo = info
      }).catch(() => {})
    }
  },
  methods: {
    ...mapActions(['getPrefs', 'savePrefs', 'saveLayout']),
    orderStyle,
    timeStyle,
    initialsStyle,
    isOpen (id) {
      return this.open.includes(id)
    },
    toggle (id) {
      this.open = this.isOpen(id) ? this.open.filter(o => o !== id) : [...this.open, id]
      try {
        localStorage.setItem(OPEN_KEY, JSON.stringify(this.open))
      } catch (err) {}
    },
    // The closed Kalender row's colour sample: the two preview orders as
    // small boxes, their lines thinned to fit.
    swatchStyle (value, color) {
      const look = lookOf(value)
      if (value === 'vana') return { background: color }
      const style = orderStyle(look, color)
      return { ...style, boxShadow: style.boxShadow.replace(/[34]px/g, '2px') }
    },
    lookText (value) {
      return lookOf(value).text
    },
    chooseLook (value) {
      if (value !== this.prefs.calendarLook) this.savePrefs({ calendarLook: value })
    },
    choosePlanner (look) {
      if (look !== (this.prefs.plannerLook || 'uus')) this.savePrefs({ plannerLook: look })
    },
    choosePosition (position) {
      if (position !== this.layout.position) this.saveLayout({ position })
    },
    async handleForm () {
      await api.changePassword({ ...this.passwordForm })
      this.passwordForm = { ...EMPTY_PASSWORD }
      this._reset('changePassword')
      // The leak reminder goes with the old password.
      this.$store.dispatch('isAuthenticated', true).catch(() => {})
      return true
    },
    async saveSignature (dataUrl) {
      if (!dataUrl) return
      this.signatureSaving = true
      try {
        const { signature } = await api.saveMySignature(dataUrl)
        this.signature = signature
        this.showNotification({ text: 'Allkiri salvestatud', color: 'green' })
      } catch (err) {
        this.showNotification({ text: (err && err.message) || 'Allkirja salvestamine ebaõnnestus', color: 'red' })
      } finally {
        this.signatureSaving = false
      }
    },
    async deleteSignature () {
      this.confirmDelete = false
      this.signatureSaving = true
      try {
        await api.deleteMySignature()
        this.signature = null
        this.showNotification({ text: 'Allkiri kustutatud', color: 'green' })
      } catch (err) {
        this.showNotification({ text: (err && err.message) || 'Allkirja kustutamine ebaõnnestus', color: 'red' })
      } finally {
        this.signatureSaving = false
      }
    }
  }
}
</script>

<style scoped>
.me {
  --m-surface: #FFFFFF; --m-ink: #16322A; --m-ink-2: #3E504A; --m-ink-3: #5B6B66;
  --m-line: #C9D2CE; --m-line-soft: #E1E7E4; --m-green: #2E7D32; --m-blue: #1F5FD1;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif; color: var(--m-ink); max-width: 1180px; padding-bottom: 40px;
}
.me button { font: inherit; color: inherit; cursor: pointer; }
.me button:focus-visible { outline: 3px solid var(--m-blue); outline-offset: 2px; }
.me-cap { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--m-ink-3); font-weight: 600; }
.me-title { font-size: 30px; font-weight: 600; margin: 2px 0 16px; letter-spacing: -.01em; line-height: 1.2; }
.me-sec { background: var(--m-surface); border: 1px solid var(--m-line-soft); border-radius: 10px; padding: 16px; margin-bottom: 14px; }
.me-sec h2 { font-size: 16px; font-weight: 600; margin: 0 0 4px; }
.me-hint { font-size: 13px; color: var(--m-ink-3); margin: 0 0 12px; }
.me-sec--alert { border-color: #E3A39E; }
.me-pw-state { display: flex; align-items: center; gap: 6px; font-size: 13px; color: var(--m-ink-3); margin: 4px 0 8px; }
.me-pw-state--ok { color: #1B5E20; }
.me-alert { font-size: 13.5px; line-height: 1.45; color: #8C1D18; background: #FDECEA; border-radius: 8px; padding: 8px 12px; margin: 4px 0 10px; }

/* An open/close row: the heading is the button, the current choice on the right. */
.me-row { padding: 0; margin-bottom: 8px; }
.me-row--open { border-color: var(--m-line); }
.me-sec .me-row__h { margin: 0; font-size: 16px; }
.me .me-row__head {
  display: grid; grid-template-columns: minmax(0, 1fr) auto 24px; align-items: center; gap: 12px;
  width: 100%; padding: 14px 16px; border: 0; background: none; text-align: left; border-radius: 10px;
}
.me .me-row__head:hover { background: #F4F6F5; }
.me-row__title { font-size: 16px; font-weight: 600; }
.me-row__sub { display: block; font-size: 12.5px; color: var(--m-ink-3); font-weight: 400; margin-top: 2px; }
.me-row__val { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: var(--m-ink-2); white-space: nowrap; }
.me-chev { width: 10px; height: 10px; margin: 0 auto 4px; border-right: 2px solid var(--m-ink-3); border-bottom: 2px solid var(--m-ink-3); transform: rotate(45deg); transition: transform .18s ease; }
.me-row--open .me-chev { transform: rotate(225deg); margin: 4px auto 0; }
.me-row__body { padding: 0 16px 16px; }
.me-row + .me-pair { margin-top: 6px; }
.me-swatch { display: inline-grid; grid-template-columns: 1fr 1fr; gap: 2px; width: 34px; height: 22px; padding: 2px; border-radius: 4px; background: #F4F6F5; }
.me-swatch i { border-radius: 2px; }
.me-tag.me-tag--warn { color: #8A6100; background: #FFF8E6; box-shadow: inset 0 0 0 1px #E8D7A6; }
@media (prefers-reduced-motion: reduce) { .me-chev { transition: none; } }

.me-default { display: flex; align-items: center; gap: 10px; border: 1px solid var(--m-line); background: var(--m-surface); border-radius: 10px; padding: 10px 14px; margin-bottom: 10px; text-align: left; width: 100%; font-size: 14px; }
.me-radio { width: 18px; height: 18px; border-radius: 50%; border: 2px solid var(--m-line); flex: none; }
.me-default[aria-pressed="true"], .me-look[aria-pressed="true"] { border-color: var(--m-green); box-shadow: inset 0 0 0 1px var(--m-green); background: #F2F8F2; }
.me-default[aria-pressed="true"] .me-radio { border-color: var(--m-green); background: radial-gradient(var(--m-green) 0 4px, transparent 5px); }

.me-looks { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 10px; }
.me-look { display: flex; flex-direction: column; gap: 10px; border: 1px solid var(--m-line); background: var(--m-surface); border-radius: 10px; padding: 12px; text-align: left; }
.me-look__text { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 8px; }
.me-look__text b { font-size: 15px; }
.me-tag { font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--m-blue); background: #EEF3FC; padding: 1px 6px; border-radius: 999px; }
.me-note { flex-basis: 100%; font-size: 13px; color: var(--m-ink-3); line-height: 1.4; }

/* The preview: a strip of Kalender with two orders side by side. */
.me-pre { display: grid; grid-template-columns: 1fr 1fr; gap: 4px; padding: 6px; height: 104px; border-radius: 8px; background: #F4F6F5; overflow: hidden; pointer-events: none; }
.me-pre .cal-ev { display: block; overflow: hidden; min-width: 0; }
.me-pre .event-line { display: block; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.me-pre .cal-ev__time { display: flex; }
.me-pre--vana { font-family: Roboto, sans-serif; }
.me-vana { color: #fff; font-size: 12px; line-height: 15px; padding: 4px; border-radius: 4px; overflow: hidden; white-space: nowrap; }

.me-seg { display: inline-grid; grid-template-columns: repeat(3, minmax(90px, 1fr)); border: 1.5px solid var(--m-line); border-radius: 8px; overflow: hidden; }
.me-seg button { height: 40px; border: 0; border-left: 1.5px solid var(--m-line); background: var(--m-surface); font-weight: 600; font-size: 14px; }
.me-seg button:first-child { border-left: 0; }
.me-seg--2 { grid-template-columns: repeat(2, minmax(90px, 1fr)); }
.me-seg button[aria-pressed="true"] { background: var(--m-ink); color: #fff; }

/* Parool beside Allkiri and Google Kalender. */
.me-pair { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px; align-items: start; }
.me-pair .me-sec { margin-bottom: 0; }
.me-col { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
.me-form { display: flex; flex-direction: column; gap: 10px; max-width: 420px; }
.me-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.me .me-btn {
  height: 38px; padding: 0 18px; border-radius: 6px; border: 0; background: var(--m-green); color: #fff;
  font-weight: 700; font-size: 14.5px; display: inline-flex; align-items: center; text-decoration: none; align-self: flex-start;
}
.me .me-btn:disabled { opacity: .6; cursor: default; }
.me .me-btn--outline { background: var(--m-surface); color: var(--m-green); border: 1.5px solid var(--m-green); font-weight: 600; }
.me .me-btn--danger { background: var(--m-surface); color: #B3261E; border: 1.5px solid var(--m-line); font-weight: 600; }
.me-sign { display: flex; align-items: center; justify-content: center; height: 96px; background: #fff; border: 1px solid var(--m-line); border-radius: 8px; margin-bottom: 12px; font-size: 13px; color: var(--m-ink-3); }
.me-sign img { max-height: 88px; max-width: 100%; object-fit: contain; }
.me-state { display: inline-flex; align-items: center; height: 26px; padding: 0 10px; border-radius: 13px; font-size: 12.5px; font-weight: 600; background: #E9EEEC; color: var(--m-ink-2); }
.me-state--on { background: #EAF3EA; color: #1B5E20; }

@media (max-width: 900px) { .me-pair { grid-template-columns: minmax(0, 1fr); } }
@media (max-width: 600px) { .me-title { font-size: 24px; } }
@media (max-width: 760px) {
  .me .me-row__head { grid-template-columns: minmax(0, 1fr) 24px; }
  .me-row__val { grid-column: 1; grid-row: 2; }
  .me-chev { grid-column: 2; grid-row: 1 / span 2; }
  .me-row__sub { display: none; }
}
</style>
